/**
 * Voice Profile Extractor
 * 
 * Extracts profile information from voice transcripts using pattern matching.
 * This is a lightweight client-side extraction for quick profile updates.
 * 
 * For complex extraction, the backend /api/extractProfile endpoint is still
 * the source of truth.
 * 
 * @author Heseda AI Team
 * @version 1.0
 */

import type { MasterProfile, SkinType } from '../types';

/**
 * Profile extraction patterns organized by field
 */
const EXTRACTION_PATTERNS = {
  skinType: {
    ko: [
      { pattern: /(?:나|저|제)\s*(?:는|은)?\s*(?:피부\s*(?:가|는|은)?)?\s*(지성|건성|복합성|민감성|중성)/i, group: 1 },
      { pattern: /(지성|건성|복합성|민감성|중성)\s*(?:피부|타입)/i, group: 1 },
      { pattern: /피부\s*(?:가|는|은)?\s*(지성|건성|복합성|민감성|중성)/i, group: 1 },
    ],
    en: [
      { pattern: /(?:i have|my skin is)\s*(oily|dry|combination|sensitive|normal)/i, group: 1 },
      { pattern: /(oily|dry|combination|sensitive|normal)\s*skin/i, group: 1 },
    ],
    valueMap: {
      // Korean mappings
      '지성': 'oily',
      '건성': 'dry',
      '복합성': 'combination',
      '민감성': 'sensitive',
      '중성': 'normal',
      // English mappings (lowercase)
      'oily': 'oily',
      'dry': 'dry',
      'combination': 'combination',
      'sensitive': 'sensitive',
      'normal': 'normal',
    } as Record<string, string>,
  },
  
  concerns: {
    ko: [
      { pattern: /(모공|블랙헤드|여드름|주름|잡티|색소침착|트러블|다크서클|붉은기|각질|건조함)/gi },
    ],
    en: [
      { pattern: /(pores|blackheads|acne|wrinkles|dark spots|pigmentation|blemishes|dark circles|redness|flakes|dryness)/gi },
    ],
    valueMap: {
      // Korean mappings
      '모공': 'pores',
      '블랙헤드': 'blackheads',
      '여드름': 'acne',
      '주름': 'wrinkles',
      '잡티': 'dark_spots',
      '색소침착': 'pigmentation',
      '트러블': 'blemishes',
      '다크서클': 'dark_circles',
      '붉은기': 'redness',
      '각질': 'flakes',
      '건조함': 'dryness',
      // English mappings
      'pores': 'pores',
      'blackheads': 'blackheads',
      'acne': 'acne',
      'wrinkles': 'wrinkles',
      'dark spots': 'dark_spots',
      'pigmentation': 'pigmentation',
      'blemishes': 'blemishes',
      'dark circles': 'dark_circles',
      'redness': 'redness',
      'flakes': 'flakes',
      'dryness': 'dryness',
    } as Record<string, string>,
  },
  
  age: {
    ko: [
      { pattern: /(?:나|저|제)\s*(?:는|은)?\s*(\d{1,2})\s*(?:살|세)/i, group: 1 },
      { pattern: /(\d{1,2})\s*(?:살|세)\s*(?:이에요|입니다|야|예요)/i, group: 1 },
    ],
    en: [
      { pattern: /(?:i am|i'm)\s*(\d{1,2})\s*(?:years old)?/i, group: 1 },
      { pattern: /(\d{1,2})\s*years old/i, group: 1 },
    ],
  },
  
  gender: {
    ko: [
      { pattern: /(?:나|저|제)\s*(?:는|은)?\s*(남자|여자|남성|여성)/i, group: 1 },
    ],
    en: [
      { pattern: /(?:i am|i'm)\s*(?:a\s*)?(male|female|man|woman)/i, group: 1 },
    ],
    valueMap: {
      '남자': 'male',
      '남성': 'male',
      '여자': 'female',
      '여성': 'female',
      'male': 'male',
      'man': 'male',
      'female': 'female',
      'woman': 'female',
    } as Record<string, string>,
  },
  
  name: {
    ko: [
      { pattern: /(?:나|저|제)\s*(?:는|은)?\s*(?:이름\s*(?:이|은|는))?\s*([가-힣]{2,4})(?:\s*(?:이에요|입니다|야|예요|라고\s*해요))/i, group: 1 },
    ],
    en: [
      { pattern: /(?:my name is|i'm called|call me)\s*([A-Za-z]+)/i, group: 1 },
    ],
  },
};

/**
 * Extracted profile data from voice
 */
export interface VoiceProfileExtraction {
  /** Detected skin type */
  skinType?: string;
  /** Detected skin concerns (array) */
  concerns?: string[];
  /** Detected age */
  age?: number;
  /** Detected gender */
  gender?: string;
  /** Detected name */
  name?: string;
  /** Whether any extraction was successful */
  hasExtractions: boolean;
}

/**
 * Extract profile information from voice transcript
 * 
 * @param text - The transcribed voice text
 * @param language - The language of the transcript
 * @returns Extracted profile fields
 */
export function extractProfileFromVoice(
  text: string,
  language: 'ko' | 'en'
): VoiceProfileExtraction {
  const result: VoiceProfileExtraction = {
    hasExtractions: false,
  };
  
  // Extract skin type — voice patterns only exist for ko/en
  const patternLang: 'ko' | 'en' = language === 'ko' ? 'ko' : 'en';
  const skinTypePatterns = EXTRACTION_PATTERNS.skinType[patternLang];
  for (const { pattern, group } of skinTypePatterns) {
    const match = text.match(pattern);
    if (match && match[group]) {
      const rawValue = match[group].toLowerCase();
      const mapped = EXTRACTION_PATTERNS.skinType.valueMap[rawValue] || 
                     EXTRACTION_PATTERNS.skinType.valueMap[match[group]];
      if (mapped) {
        result.skinType = mapped;
        result.hasExtractions = true;
        break;
      }
    }
  }
  
  // Extract concerns (can be multiple)
  const concernPatterns = EXTRACTION_PATTERNS.concerns[patternLang];
  const concerns: string[] = [];
  for (const { pattern } of concernPatterns) {
    const matches = text.matchAll(pattern);
    for (const match of matches) {
      const rawValue = match[0].toLowerCase();
      const mapped = EXTRACTION_PATTERNS.concerns.valueMap[rawValue] ||
                     EXTRACTION_PATTERNS.concerns.valueMap[match[0]];
      if (mapped && !concerns.includes(mapped)) {
        concerns.push(mapped);
      }
    }
  }
  if (concerns.length > 0) {
    result.concerns = concerns;
    result.hasExtractions = true;
  }
  
  // Extract age
  const agePatterns = EXTRACTION_PATTERNS.age[language];
  for (const { pattern, group } of agePatterns) {
    const match = text.match(pattern);
    if (match && match[group]) {
      const age = parseInt(match[group], 10);
      if (age >= 13 && age <= 100) { // Reasonable age range
        result.age = age;
        result.hasExtractions = true;
        break;
      }
    }
  }
  
  // Extract gender
  const genderPatterns = EXTRACTION_PATTERNS.gender[language];
  for (const { pattern, group } of genderPatterns) {
    const match = text.match(pattern);
    if (match && match[group]) {
      const rawValue = match[group].toLowerCase();
      const mapped = EXTRACTION_PATTERNS.gender.valueMap[rawValue] ||
                     EXTRACTION_PATTERNS.gender.valueMap[match[group]];
      if (mapped) {
        result.gender = mapped;
        result.hasExtractions = true;
        break;
      }
    }
  }
  
  // Extract name
  const namePatterns = EXTRACTION_PATTERNS.name[language];
  for (const { pattern, group } of namePatterns) {
    const match = text.match(pattern);
    if (match && match[group]) {
      result.name = match[group];
      result.hasExtractions = true;
      break;
    }
  }
  
  return result;
}

/**
 * Merge voice extractions into master profile
 * Only updates fields that are not already set or confirms existing values
 * 
 * @param profile - Current master profile
 * @param extraction - Voice profile extraction
 * @returns Updated profile (new object if changes were made)
 */
export function mergeVoiceExtractionIntoProfile(
  profile: Partial<MasterProfile>,
  extraction: VoiceProfileExtraction
): { profile: Partial<MasterProfile>; changed: boolean; changes: string[] } {
  if (!extraction.hasExtractions) {
    return { profile, changed: false, changes: [] };
  }
  
  const newProfile = { ...profile };
  const changes: string[] = [];
  let changed = false;
  
  // Merge skin type
  if (extraction.skinType) {
    if (!newProfile.skin) {
      newProfile.skin = {};
    }
    if (newProfile.skin.type !== extraction.skinType) {
      newProfile.skin = { ...newProfile.skin, type: extraction.skinType as SkinType };
      changes.push(`피부 타입: ${extraction.skinType}`);
      changed = true;
    }
  }
  
  // Merge concerns (append new ones)
  if (extraction.concerns && extraction.concerns.length > 0) {
    if (!newProfile.skin) {
      newProfile.skin = {};
    }
    const existingConcerns = newProfile.skin.concerns || [];
    const newConcerns = extraction.concerns.filter(c => !existingConcerns.includes(c));
    if (newConcerns.length > 0) {
      newProfile.skin = {
        ...newProfile.skin,
        concerns: [...existingConcerns, ...newConcerns],
      };
      changes.push(`새로운 고민: ${newConcerns.join(', ')}`);
      changed = true;
    }
  }
  
  // Merge age
  if (extraction.age) {
    if (!newProfile.identity) {
      newProfile.identity = {};
    }
    if (newProfile.identity.age !== extraction.age) {
      newProfile.identity = { ...newProfile.identity, age: extraction.age };
      changes.push(`나이: ${extraction.age}세`);
      changed = true;
    }
  }
  
  // Merge gender
  if (extraction.gender) {
    if (!newProfile.identity) {
      newProfile.identity = {};
    }
    if (newProfile.identity.gender !== extraction.gender) {
      newProfile.identity = { ...newProfile.identity, gender: extraction.gender };
      changes.push(`성별: ${extraction.gender}`);
      changed = true;
    }
  }
  
  // Merge name
  if (extraction.name) {
    if (!newProfile.identity) {
      newProfile.identity = {};
    }
    if (!newProfile.identity.name) { // Only set if not already set
      newProfile.identity = { ...newProfile.identity, name: extraction.name };
      changes.push(`이름: ${extraction.name}`);
      changed = true;
    }
  }
  
  return { profile: newProfile, changed, changes };
}
